import type { MapDef } from '../world/mapdef';
import { T } from '../world/tiles';
import { hash2, noise1, noise2, smoothstep } from './noise';

type Ctx = CanvasRenderingContext2D;

// ---------------------------------------------------------------------------
// Wind
// ---------------------------------------------------------------------------

/** Global wind: a slowly varying breeze with gusts that sweep across the map from the west. */
let windT = 0;
export const wind = {
  update(dt: number): void {
    windT += dt;
  },
  time(): number {
    return windT;
  },
  /** Base strength in [0, ~1.3]. */
  strength(t = windT): number {
    const breeze = 0.35 + 0.35 * noise1(t * 0.07, 1);
    const gust = smoothstep(0.62, 0.9, noise1(t * 0.22, 2)) * 0.75;
    return breeze + gust;
  },
  /** Wind felt at a point: gusts arrive later further east. */
  at(x: number, y: number, t = windT): number {
    return wind.strength(t - x / 240 - y / 900);
  },
  /** Fast flutter for leaves and blades. */
  flutter(x: number, y: number, t = windT): number {
    return Math.sin(t * 3.1 + x * 0.09 + y * 0.05) * 0.6 + Math.sin(t * 5.3 + x * 0.21) * 0.4;
  },
};

// ---------------------------------------------------------------------------
// Grass blades and flowers (drawn every frame so they can sway)
// ---------------------------------------------------------------------------

interface Blade {
  x: number;
  y: number;
  h: number;
  lean: number;
  tone: number;
}

interface Flower {
  x: number;
  y: number;
  h: number;
  color: string;
  kind: number;
}

export interface Foliage {
  blades: Blade[];
  flowers: Flower[];
}

const FLOWER_COLORS = ['#ff7aa0', '#ffd54f', '#ffffff', '#c59bff', '#ffa94d', '#7fc8ff'];
const BLADE_TONES = ['#3f8a3a', '#56a548', '#78c25a'];

export function buildFoliage(map: MapDef): Foliage {
  const blades: Blade[] = [];
  const flowers: Flower[] = [];
  if (map.indoor) return { blades, flowers };
  for (let ty = 0; ty < map.h; ty++) {
    for (let tx = 0; tx < map.w; tx++) {
      const ch = map.rows[ty][tx];
      if (ch !== 'g' && ch !== 'G' && ch !== 'F') continue;
      const below = map.rows[ty + 1]?.[tx];
      const n = ch === 'G' ? 5 : ch === 'F' ? 2 : 3;
      for (let k = 0; k < n; k++) {
        if (ch === 'g' && hash2(tx * 9 + k, ty, 301) > 0.5) continue;
        const x = tx * T + hash2(tx, ty * 9 + k, 302) * T;
        const y = ty * T + 3 + hash2(tx * 9 + k, ty * 3, 303) * (below === 'g' || below === 'G' || below === 'F' ? T : T - 4);
        blades.push({ x, y, h: 3 + hash2(tx + k, ty, 304) * (ch === 'F' ? 2 : 4), lean: (hash2(tx, ty + k, 305) - 0.5) * 2, tone: Math.floor(hash2(tx + k * 3, ty, 306) * 3) });
      }
      if (ch === 'G' || (ch === 'g' && hash2(tx, ty, 307) < 0.08)) {
        const m = ch === 'G' ? 4 : 1;
        for (let k = 0; k < m; k++) {
          flowers.push({
            x: tx * T + 2 + hash2(tx * 5 + k, ty, 308) * 12,
            y: ty * T + 4 + hash2(tx, ty * 5 + k, 309) * 11,
            h: 3 + hash2(tx + k, ty * 2, 310) * 3,
            color: FLOWER_COLORS[Math.floor(hash2(tx * 3 + k, ty * 7, 311) * FLOWER_COLORS.length)],
            kind: Math.floor(hash2(tx + k * 7, ty, 312) * 3),
          });
        }
      }
    }
  }
  blades.sort((a, b) => a.y - b.y);
  return { blades, flowers };
}

export interface Pusher {
  x: number;
  y: number;
}

/** Sway with the wind and bend away from people walking through. */
function bendAt(x: number, y: number, pushers: readonly Pusher[]): number {
  let b = wind.at(x, y) * 1.6 + wind.flutter(x, y) * 0.6;
  for (const p of pushers) {
    const dx = x - p.x, dy = (y - p.y) * 1.6;
    const d2 = dx * dx + dy * dy;
    if (d2 < 200) b += Math.sign(dx || 1) * (1 - d2 / 200) * 4;
  }
  return b;
}

export function drawFoliage(c: Ctx, f: Foliage, view: { x0: number; x1: number; y0: number; y1: number }, pushers: readonly Pusher[]): void {
  c.lineWidth = 0.75;
  c.lineCap = 'round';
  for (let tone = 0; tone < 3; tone++) {
    c.strokeStyle = BLADE_TONES[tone];
    c.beginPath();
    for (const b of f.blades) {
      if (b.tone !== tone || b.x < view.x0 || b.x > view.x1 || b.y < view.y0 || b.y > view.y1) continue;
      const bend = bendAt(b.x, b.y, pushers) + b.lean;
      c.moveTo(b.x, b.y);
      c.quadraticCurveTo(b.x + bend * 0.2, b.y - b.h * 0.6, b.x + bend * 0.7, b.y - b.h);
    }
    c.stroke();
  }
  c.strokeStyle = '#3f8a3a';
  c.beginPath();
  const heads: [number, number, Flower][] = [];
  for (const fl of f.flowers) {
    if (fl.x < view.x0 || fl.x > view.x1 || fl.y < view.y0 || fl.y > view.y1) continue;
    const bend = bendAt(fl.x, fl.y, pushers) * 0.8;
    const hx = fl.x + bend * 0.6, hy = fl.y - fl.h;
    c.moveTo(fl.x, fl.y);
    c.quadraticCurveTo(fl.x + bend * 0.15, fl.y - fl.h * 0.6, hx, hy);
    heads.push([hx, hy, fl]);
  }
  c.stroke();
  for (const [hx, hy, fl] of heads) {
    c.fillStyle = fl.color;
    if (fl.kind === 0) {
      for (let k = 0; k < 5; k++) {
        const a = (k / 5) * Math.PI * 2;
        c.beginPath();
        c.arc(hx + Math.cos(a) * 0.9, hy + Math.sin(a) * 0.9, 0.7, 0, Math.PI * 2);
        c.fill();
      }
      c.fillStyle = '#f6b52e';
      c.beginPath();
      c.arc(hx, hy, 0.55, 0, Math.PI * 2);
      c.fill();
    } else if (fl.kind === 1) {
      c.beginPath();
      c.ellipse(hx, hy, 1.1, 1.4, 0, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = 'rgba(255,255,255,0.5)';
      c.fillRect(hx - 0.5, hy - 1, 0.5, 0.5);
    } else {
      c.beginPath();
      c.arc(hx - 0.6, hy, 0.7, 0, Math.PI * 2);
      c.arc(hx + 0.6, hy, 0.7, 0, Math.PI * 2);
      c.arc(hx, hy - 0.7, 0.7, 0, Math.PI * 2);
      c.fill();
    }
  }
}

// ---------------------------------------------------------------------------
// Trees: high-detail canopies cached once, then bent by the wind every frame
// ---------------------------------------------------------------------------

export type TreeKind = 'tree' | 'cherry' | 'pine';

interface Canopy {
  cv: HTMLCanvasElement;
  w: number;
  h: number;
  /** Canopy image anchor (bottom-centre of the crown) relative to the trunk base. */
  ax: number;
  ay: number;
}

const canopyCache = new Map<string, Canopy>();
let detail = 2;

export function setDetail(z: number): void {
  if (z !== detail) canopyCache.clear();
  detail = z;
}

function blob(c: Ctx, x: number, y: number, r: number, fill: string): void {
  c.fillStyle = fill;
  c.beginPath();
  c.arc(x, y, r, 0, Math.PI * 2);
  c.fill();
}

/** Paint a leafy crown out of many small clusters, lit from the top-left. */
function paintCrown(c: Ctx, w: number, h: number, kind: TreeKind, variant: number): void {
  const cx = w / 2, cy = h * 0.55;
  const pal =
    kind === 'cherry'
      ? { dark: '#b9577f', mid: '#e98aae', light: '#ffc6da', rim: '#fff0f5', hole: '#7a3550', accent: '#ffffff' }
      : { dark: '#255f30', mid: '#3c8f45', light: '#6cbf5c', rim: '#b7e88f', hole: '#163d1e', accent: '#8fd36f' };
  const seed = variant * 97 + (kind === 'cherry' ? 5 : 1);
  const R = w * 0.42;
  const pts: [number, number, number][] = [];
  for (let i = 0; i < 46; i++) {
    const a = hash2(i, seed, 401) * Math.PI * 2;
    const r = Math.sqrt(hash2(i, seed, 402)) * R;
    pts.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r * 0.82, 3 + hash2(i, seed, 403) * 3.5]);
  }
  // Silhouette + dark depth layer.
  for (const [x, y, r] of pts) blob(c, x, y + 1.2, r + 0.9, 'rgba(20,30,15,0.85)');
  for (const [x, y, r] of pts) blob(c, x, y + 0.8, r, pal.dark);
  // Mid tones, shifted up-left by the light.
  for (const [x, y, r] of pts) {
    const lit = (cx - x) / R * 0.5 + (cy - y) / R * 0.7;
    if (lit > -0.35) blob(c, x - 0.6, y - 0.6, r * 0.82, pal.mid);
  }
  // Highlights on the top-left of each cluster.
  for (const [x, y, r] of pts) {
    const lit = (cx - x) / R * 0.5 + (cy - y) / R * 0.7;
    if (lit > 0.05) blob(c, x - r * 0.35, y - r * 0.35, r * 0.45, pal.light);
    if (lit > 0.4) blob(c, x - r * 0.5, y - r * 0.5, r * 0.18, pal.rim);
  }
  // Little gaps showing depth, and leaf/blossom speckles.
  for (let i = 0; i < 10; i++) {
    const x = cx + (hash2(i, seed, 404) - 0.5) * R * 1.4, y = cy + (hash2(i, seed, 405) - 0.2) * R * 0.9;
    blob(c, x, y, 0.8, pal.hole);
  }
  for (let i = 0; i < (kind === 'cherry' ? 70 : 24); i++) {
    const a = hash2(i, seed, 406) * Math.PI * 2, r = Math.sqrt(hash2(i, seed, 407)) * R * 1.05;
    const x = cx + Math.cos(a) * r, y = cy + Math.sin(a) * r * 0.82;
    c.fillStyle = hash2(i, seed, 408) < 0.5 ? pal.accent : pal.light;
    c.globalAlpha = 0.7;
    c.fillRect(x, y, 0.75, 0.75);
    c.globalAlpha = 1;
  }
}

function paintPine(c: Ctx, w: number, h: number, variant: number): void {
  const cx = w / 2;
  for (let i = 0; i < 5; i++) {
    const tierW = w * (0.95 - i * 0.16), y = h - 4 - i * h * 0.17, th = h * 0.3;
    c.fillStyle = 'rgba(15,30,15,0.85)';
    c.beginPath();
    c.moveTo(cx - tierW / 2 - 0.8, y + 1);
    c.quadraticCurveTo(cx, y - th * 0.4, cx, y - th - 0.8);
    c.quadraticCurveTo(cx, y - th * 0.4, cx + tierW / 2 + 0.8, y + 1);
    c.fill();
    const g = c.createLinearGradient(cx - tierW / 2, 0, cx + tierW / 2, 0);
    g.addColorStop(0, '#4f9a5a');
    g.addColorStop(0.45, '#2f6e3e');
    g.addColorStop(1, '#1f4d2b');
    c.fillStyle = g;
    c.beginPath();
    c.moveTo(cx - tierW / 2, y);
    c.quadraticCurveTo(cx, y - th * 0.4, cx, y - th);
    c.quadraticCurveTo(cx, y - th * 0.4, cx + tierW / 2, y);
    c.quadraticCurveTo(cx, y - 2.5, cx - tierW / 2, y);
    c.fill();
    // Needle tufts along the edge.
    for (let k = 0; k < 8; k++) {
      const t = k / 7;
      const nx = cx - tierW / 2 + t * tierW, ny = y - Math.sin(t * Math.PI) * 2.2;
      c.fillStyle = t < 0.5 ? '#7cc47a' : '#2a5c34';
      c.fillRect(nx, ny - 0.5, 0.75, 1.5);
    }
    void variant;
  }
}

export function canopy(kind: TreeKind, variant: number, scale = 1): Canopy {
  const key = `${kind}:${variant}:${scale}:${detail}`;
  let cp = canopyCache.get(key);
  if (cp) return cp;
  const w = (kind === 'pine' ? 30 : 46) * scale, h = (kind === 'pine' ? 54 : 42) * scale;
  const cv = document.createElement('canvas');
  cv.width = Math.ceil(w * detail);
  cv.height = Math.ceil(h * detail);
  const c = cv.getContext('2d')!;
  c.scale(detail * scale, detail * scale);
  if (kind === 'pine') paintPine(c, w / scale, h / scale, variant);
  else paintCrown(c, w / scale, h / scale, kind, variant);
  cp = { cv, w, h, ax: w / 2, ay: kind === 'pine' ? h : h * 0.92 };
  canopyCache.set(key, cp);
  return cp;
}

/** Draw a full tree with its trunk at (x, y); the crown leans with the wind. */
export function drawTree(c: Ctx, x: number, y: number, kind: TreeKind, variant: number, scale = 1): void {
  const w = wind.at(x, y);
  const sway = (w * 0.9 + wind.flutter(x, y) * 0.25) * scale;
  // Trunk with bark shading and roots.
  const th = (kind === 'pine' ? 12 : 22) * scale, tw = (kind === 'pine' ? 5 : 7) * scale;
  const g = c.createLinearGradient(x - tw / 2, 0, x + tw / 2, 0);
  g.addColorStop(0, '#9a6a44');
  g.addColorStop(0.5, '#6b4024');
  g.addColorStop(1, '#3e2414');
  c.fillStyle = '#2a1810';
  c.fillRect(x - tw / 2 - 0.5, y - th, tw + 1, th);
  c.fillStyle = g;
  c.beginPath();
  c.moveTo(x - tw / 2 - 1.5, y);
  c.quadraticCurveTo(x - tw / 2, y - 2, x - tw / 2 + sway * 0.1, y - th);
  c.lineTo(x + tw / 2 + sway * 0.1, y - th);
  c.quadraticCurveTo(x + tw / 2, y - 2, x + tw / 2 + 1.5, y);
  c.closePath();
  c.fill();
  c.fillStyle = 'rgba(40,20,10,0.5)';
  for (let i = 0; i < 4; i++) c.fillRect(x - tw / 4 + (i % 2) * 2, y - th + 3 + i * 4 * scale, 0.5, 2.5);
  // Crown, sheared by the wind around its base.
  const cp = canopy(kind, variant, scale);
  const baseY = y - th + (kind === 'pine' ? 4 : 6) * scale;
  c.save();
  c.translate(x, baseY);
  c.transform(1, 0, -sway * 0.05, 1, 0, 0);
  c.rotate(sway * 0.012);
  c.drawImage(cp.cv, -cp.ax, -cp.ay, cp.w, cp.h);
  c.restore();
}

// ---------------------------------------------------------------------------
// Cloud shadows drifting over outdoor maps
// ---------------------------------------------------------------------------

export class Clouds {
  private items: { x: number; y: number; r: number; seed: number }[] = [];

  constructor(private w: number, private h: number) {
    for (let i = 0; i < 6; i++) this.items.push({ x: Math.random() * w, y: Math.random() * h, r: 70 + Math.random() * 80, seed: i });
  }

  update(dt: number): void {
    const speed = 6 + wind.strength() * 14;
    for (const cl of this.items) {
      cl.x += speed * dt;
      cl.y += speed * 0.25 * dt;
      if (cl.x - cl.r > this.w) cl.x = -cl.r;
      if (cl.y - cl.r > this.h) cl.y = -cl.r;
    }
  }

  draw(c: Ctx, view: { x0: number; x1: number; y0: number; y1: number }, alpha: number): void {
    if (alpha <= 0) return;
    c.save();
    c.globalCompositeOperation = 'multiply';
    for (const cl of this.items) {
      if (cl.x + cl.r * 1.5 < view.x0 || cl.x - cl.r * 1.5 > view.x1 || cl.y + cl.r < view.y0 || cl.y - cl.r > view.y1) continue;
      for (let k = 0; k < 4; k++) {
        const ox = (noise2(cl.seed, k, 501) - 0.5) * cl.r * 1.2, oy = (noise2(k, cl.seed, 502) - 0.5) * cl.r * 0.6;
        const r = cl.r * (0.55 + noise2(k, cl.seed * 3, 503) * 0.4);
        const g = c.createRadialGradient(cl.x + ox, cl.y + oy, 0, cl.x + ox, cl.y + oy, r);
        g.addColorStop(0, `rgba(60,70,95,${0.16 * alpha})`);
        g.addColorStop(1, 'rgba(60,70,95,0)');
        c.fillStyle = g;
        c.fillRect(cl.x + ox - r, cl.y + oy - r, r * 2, r * 2);
      }
    }
    c.restore();
  }
}
